import React, {useEffect, useState, useRef, useContext} from 'react'
import {
  Container,
  Button,
  Grid,
  Typography,
  Box,
  Card,
  TextField,
  CircularProgress,
  Drawer,
} from '@material-ui/core'
import WizardPreview from '../BarcodeWizardPreview'
import BarcodeNavHeaderButtons from '../BarcodeNavHeaderButtons'
import TemplateDrawer from '../BarcodeNavHeaderButtons/BarcodeTemplateDrawer'
import {Mutation} from 'react-apollo'
import {sendPdfRequest} from '../../graphql/barcode/mutations/sendPdfRequestMutation'
import {libraryPageQuery} from '../../graphql/barcode/queries/libraryPageQuery'
import {useTranslation} from 'react-i18next'
import useStyles from './BarcodeLibraryGenerateAuto.styles'
import {BarcodeSheetRecordContext} from '../../context/BarcodeSheetRecord'
import {ToastMessageContext} from '../../context/ToastMessage'
import {UserContext} from '../../context/UserContext'
import {validateBarcodePermission} from '../../modules/common/utils/permissions'

export default function BarcodeLibraryGenerateAuto(props) {
  const classes = useStyles()
  const {t} = useTranslation()
  const {barcodeSheetRecord} = useContext(BarcodeSheetRecordContext)
  const accessToken = JSON.parse(window.localStorage.getItem('token'))
  const [nameError] = useState(false)
  const [barcodeSettingRecord, setBarcodeSettingRecord] = useState(props.location.state.barcodeData)
  const userId = parseInt(window.localStorage.getItem('user_id'))
  const userEmail = window.localStorage.getItem('user_email')
  const [disableOnChange] = useState(false)
  const [isNewDownload, setDownload] = useState(false)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const toast = useContext(ToastMessageContext)
  const currentUser = React.useContext(UserContext)

  const [formValues, setFormValues] = useState({
    totalGenerate: '1',
    startPosition: '1',
    totalRepeat: '1',
    maxPosition: '1',
  })

  const refBtnGenerate = useRef()

  useEffect(() => {
    if (props.location.state.barcodeData !== barcodeSettingRecord) {
      setBarcodeSettingRecord(props.location.state.barcodeData)
    } else {
      if (window !== window.parent) {
        window.parent.postMessage('scrollTop', process.env.REACT_APP_BARCODE_WINDOW_URL)
      } else {
        window.scrollTo(0, 0)
      }
    }
  }, [props.location.state.barcodeData, barcodeSettingRecord])

  const handleChange = textField => event => {
    setFormValues({
      ...formValues,
      [textField]: event.target.value,
    })
  }

  const changePosition = (value, maxPosition) => {
    setFormValues({
      ...formValues,
      startPosition: value,
      maxPosition: maxPosition,
    })
  }

  function checkValidity() {
    let errorMessage = ''

    if (formValues.totalRepeat === '') {
      errorMessage = t('barcode.library.generate-automatic.repeat-label')
    } else if (formValues.totalGenerate === '') {
      errorMessage = t('barcode.library.generate-automatic.label-blank')
    } else if (formValues.startPosition === '') {
      errorMessage = t('barcode.library.generate-automatic.position-blank')
    } else if (parseInt(formValues.totalRepeat) < 1) {
      errorMessage = t('barcode.library.generate-automatic.repeat-label-less-than')
    } else if (parseInt(formValues.totalGenerate) < 1) {
      errorMessage = t('barcode.library.generate-automatic.label-less-than')
    } else if (parseInt(formValues.startPosition) < 1) {
      errorMessage = t('barcode.library.generate-automatic.position-less-than')
    } else if (parseInt(formValues.totalRepeat) > 5) {
      errorMessage = t('barcode.library.generate-automatic.repeat-label-equal')
    } else if (
      parseInt(formValues.startPosition) >
      barcodeSheetRecord.rows * barcodeSheetRecord.columns
    ) {
      errorMessage = t('barcode.library.generate-automatic.out-of-range')
    }

    if (errorMessage !== '') {
      toast.popMessage(errorMessage, 'error', refBtnGenerate.current)
      return false
    }
    return true
  }

  function generatePdf(sendPdfRequest) {
    if(!validateBarcodePermission(currentUser, "can_generate")){
      return;
    }
    if (checkValidity()) {
      setDownload(true)
      sendPdfRequest({
        variables: {
          id: barcodeSettingRecord.id,
          accountId: barcodeSettingRecord.accountId,
          userId: userId,
          token: accessToken,
          userEmail: userEmail,
          barcodeLabelId: barcodeSettingRecord.barcodeLabelId,
          output: formValues.output,
          digits: parseInt(formValues.digits),
          prefix: formValues.prefix,
          labelsToBeGenerated: parseInt(formValues.totalGenerate),
          totalRepeat: parseInt(formValues.totalRepeat),
          startPosition: parseInt(formValues.startPosition),
          dataSource: props.showTable === 'automated' ? 'automated' : 'from_objects',
          fieldId: props.showTable === 'automated' ? null : parseInt(formValues.field),
          entityId: props.showTable === 'automated' ? null : parseInt(formValues.group),
        },
      })
    }
  }

  function chooseTemplateBtn(event) {
    setDrawerOpen(true)
  }

  const toggleDrawer = open => event => {
    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return
    }

    setDrawerOpen(open)
  }

  return (
    <div>
      <BarcodeNavHeaderButtons
        previous={props.location.state.previous}
        template={barcodeSettingRecord}
      />

      <Grid container>
        <Grid item xs={6}>
          <Box marginTop={-8} />
          <Container>
            <Card>
              <Grid container>
                <Grid container item xs={8} justify="flex-start">
                  <Typography variant="h5">
                    <div className={classes.titleStyle}>
                      {t('barcode.library.generate-automatic.template-details')}
                    </div>
                  </Typography>
                </Grid>
                <Grid container item xs={4} justify="flex-end" margin="auto">
                  <Box marginTop={3} marginRight={3}>
                    <Button
                      color="primary"
                      className={classes.buttonLink}
                      onClick={chooseTemplateBtn}
                    >
                      {t('barcode.library.generate-automatic.choose-template')}
                    </Button>
                  </Box>
                </Grid>
                <Grid container justify="flex-start">
                  <div className={classes.descriptionStyle}>
                    {t('barcode.library.generate-automatic.sheet-template')}
                  </div>
                </Grid>

                <Grid container justify="flex-start">
                  <div className={classes.informationStyle}>{barcodeSettingRecord.sheet.name}</div>
                </Grid>

                <Grid container justify="flex-start">
                  <div className={classes.descriptionStyle}>
                    {t('barcode.library.generate-automatic.label-template')}
                  </div>
                </Grid>
                <Grid container justify="flex-start">
                  <div className={classes.informationStyle}>{barcodeSettingRecord.label.name}</div>
                </Grid>

                <Grid container item xs={12} justify="flex-start">
                  <Typography variant="h5">
                    <div className={classes.titleStyle}>
                      {t('barcode.library.generate-automatic.barcode-automatic')}
                    </div>
                  </Typography>
                </Grid>

                <Grid container>
                  <Grid container item xs={6}>
                    <Grid container>
                      <div className={classes.descriptionStyle}>
                        {t('barcode.library.generate-automatic.name')}
                      </div>
                    </Grid>

                    <div className={classes.informationStyle}>{barcodeSettingRecord.name}</div>
                  </Grid>

                  <Grid container item xs={6}>
                    <Grid container>
                      <div className={classes.descriptionStyle}>
                        {t('barcode.library.generate-automatic.type')}
                      </div>
                    </Grid>

                    <div className={classes.informationStyle}>
                      {barcodeSettingRecord.barcodeType}
                    </div>
                  </Grid>

                  <Grid container item xs={6}>
                    <Grid container>
                      <div className={classes.descriptionStyle}>
                        {t('barcode.library.generate-automatic.label-template')}
                      </div>
                    </Grid>

                    <div className={classes.informationStyle}>
                      {barcodeSettingRecord.label.name}
                    </div>
                  </Grid>

                  <Grid container item xs={6}>
                    <Grid container>
                      <div className={classes.descriptionStyle}>
                        {t('barcode.library.generate-automatic.digits')}
                      </div>
                    </Grid>

                    <div className={classes.informationStyle}>{barcodeSettingRecord.digits}</div>
                  </Grid>

                  <Grid container item xs={6}>
                    <Grid container>
                      <div className={classes.descriptionStyle}>
                        {t('barcode.library.generate-automatic.start-number')}
                      </div>
                    </Grid>

                    <div className={classes.informationStyle}>
                      {barcodeSettingRecord.startNumber}
                    </div>
                  </Grid>

                  <Grid container item xs={6}>
                    <Grid container>
                      <div className={classes.descriptionStyle}>
                        {t('barcode.library.generate-automatic.prefix')}
                      </div>
                    </Grid>

                    <div className={classes.informationStyle}>
                      {barcodeSettingRecord.prefix ? barcodeSettingRecord.prefix : 'None'}
                    </div>
                  </Grid>

                  <Grid container item xs={6}>
                    <Grid container>
                      <div className={classes.descriptionStyle}>
                        {t('barcode.library.generate-automatic.last-code')}
                      </div>
                    </Grid>

                    <div className={classes.informationStyle}>
                      {barcodeSettingRecord.lastPrinted}
                    </div>
                  </Grid>

                  <Grid container item xs={6}>
                    <Grid container>
                      <div className={classes.descriptionStyle}>
                        {t('barcode.library.generate-automatic.next-code')}
                      </div>
                    </Grid>

                    <div className={classes.informationStyle}>
                      {barcodeSettingRecord.nextToPrint}
                    </div>
                  </Grid>

                  <Grid container item xs={6}>
                    <Grid container>
                      <div className={classes.descriptionStyle}>
                        {t('barcode.library.generate-automatic.output')}
                      </div>
                    </Grid>

                    <div className={classes.informationStyle}>
                      {barcodeSettingRecord.output.toUpperCase()}
                    </div>
                  </Grid>

                  <Grid container item xs={6} justify="flex-end" margin="auto">
                    <Grid container />
                  </Grid>
                </Grid>
              </Grid>
            </Card>

            <Grid container className={classes.textFieldStyle}>
              <Grid container item xs={7}>
                <TextField
                  id="total-generate"
                  label={t('barcode.library.generate-automatic.how-many-labels')}
                  value={formValues.totalGenerate}
                  fullWidth
                  type="number"
                  variant="outlined"
                  onChange={handleChange('totalGenerate')}
                  error={nameError}
                />
              </Grid>

              <Grid container item xs={1} />

              <Grid
                container
                item
                xs={4}
                justify="flex-end"
                hidden={barcodeSettingRecord.output === 'PNG'}
              >
                <TextField
                  id="start-position"
                  label={t('barcode.library.generate-automatic.start-position-sheet')}
                  value={formValues.startPosition}
                  fullWidth
                  type="number"
                  variant="outlined"
                  onChange={handleChange('startPosition')}
                  error={nameError}
                />
              </Grid>

              <Grid
                container
                item
                xs={7}
                className={classes.textFieldStyle}
                hidden={barcodeSettingRecord.output === 'PNG'}
              >
                <TextField
                  id="total-repeat"
                  label={t('barcode.library.generate-automatic.how-many-repeat')}
                  value={formValues.totalRepeat}
                  fullWidth
                  variant="outlined"
                  type="number"
                  onChange={handleChange('totalRepeat')}
                  disabled={disableOnChange}
                  error={nameError}
                />
              </Grid>
              <Grid container item xs={12} className={classes.textFieldStyle} justify="flex-end">
                <Mutation
                  mutation={sendPdfRequest}
                  refetchQueries={() => [
                    {
                      query: libraryPageQuery,
                      variables: {
                        accountId: barcodeSettingRecord.accountId,
                        token: accessToken,
                        userId: userId,
                        filter: '',
                      },
                    },
                  ]}
                  awaitRefetchQueries
                >
                  {(sendPdfRequest, {loading, data}) => {
                    if (data && isNewDownload) {
                      setDownload(false)
                      if (
                        data.sendPdfRequest.errors.length === 1 &&
                        data.sendPdfRequest.pdfData.location.length > 0
                      ) {
                        window.location.href = `${data.sendPdfRequest.pdfData.location}`
                      } else {
                        toast.popMessage(
                          data.sendPdfRequest.errors[0],
                          'error',
                          refBtnGenerate.current
                        )
                      }
                    }

                    return (
                      <Button
                        onClick={e => generatePdf(sendPdfRequest)}
                        value="generate"
                        variant="contained"
                        color="primary"
                        disabled={!validateBarcodePermission(currentUser, "can_generate")}
                        ref={refBtnGenerate}
                      >
                        {loading ? (
                          <div>
                            {t('barcode.library.generate-automatic.generating')}
                            <CircularProgress size={20} color="secondary" />
                          </div>
                        ) : (
                          t('barcode.library.generate-automatic.finish-generate')
                        )}
                      </Button>
                    )
                  }}
                </Mutation>
              </Grid>
            </Grid>
          </Container>
          <Box marginBottom={5} />
        </Grid>

        <Grid item xs={6}>
          <WizardPreview
            page="generate"
            barcodeStartPosition={formValues.startPosition}
            changePosition={changePosition}
            viewPNG={barcodeSettingRecord.output === 'PNG'}
          />
        </Grid>
      </Grid>
      <Drawer anchor="right" open={drawerOpen}>
        <TemplateDrawer toggleDrawer={toggleDrawer()} template={barcodeSettingRecord} />
      </Drawer>
    </div>
  )
}
